import { useUser } from '@cdab/scania/qpr/contexts/user-provider'
import type { CreateADHLinkData } from '@cdab/scania/qpr/interactor'
import type { Deviation } from '@cdab/scania/qpr/schema'
import { sortFnByAuditPointNo } from '@cdab/scania/qpr/utils'
import {
  checkStringLength,
  formatISODate,
  removeHtmlTagsFromString
} from '@cdab/utils'
import {
  TdsButton,
  TdsCheckbox,
  TdsChip,
  TdsIcon,
  TdsMessage,
  TdsModal,
  TdsTextField
} from '@scania/tegel-react'
import { observer } from 'mobx-react-lite'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  StyledDeviationsList,
  StyledDiv,
  StyledMessageWrapper,
  StyledModalContent,
  StyledParagraph
} from './adh-link-create-form.styles'
import type { ADHLinkCreateFormProps } from './adh-link-create-form.types'

const DEFAULT_LANGUAGE_ID = 2

const StyledFieldset = styled.fieldset`
  border: none;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const FlexContainer = styled.div`
  display: flex;
  gap: 4px;
`

const filterDeviations = (deviations: Deviation[]) => {
  return deviations
    .filter(deviation => deviation.id !== undefined)
    .filter(deviation => deviation.deviationWithoutActions === false)
    .filter(deviation => deviation.draft === false)
}

export const ADHLinkCreateForm = observer(
  ({
    selector,
    onSubmit,
    audit,
    links
  }: Readonly<ADHLinkCreateFormProps>): JSX.Element => {
    const { t } = useTranslation(['audit', 'errors'])
    const userContext = useUser()

    const [stateName, setStateName] = useState<boolean>(true)
    const [stateActionPlans, setStateActionPlans] = useState<boolean>(true)

    const defaultData = {
      key: '',
      name: '',
      languageId: userContext.userData.languageId ?? DEFAULT_LANGUAGE_ID,
      auditId: audit.id,
      did: audit.did,
      expiryDate: '',
      actionPlanItems: []
    }

    const [data, setData] = useState<CreateADHLinkData>(defaultData)

    const validateAllData = () => {
      setStateName(checkStringLength(data.name, 3, false))
      setStateActionPlans(data.actionPlanItems.length > 0)
    }

    const closeModal = () => {
      ;(
        document.querySelector(
          `[selector="${selector}"]`
        ) as HTMLTdsModalElement
      ).closeModal()
    }

    const handleSubmit = () => {
      validateAllData()
      if (
        checkStringLength(data.name, 3, false) &&
        data.actionPlanItems.length > 0
      ) {
        onSubmit(data)
        setData(defaultData)
        closeModal()
      }
    }

    const deviations = filterDeviations(audit.deviations).sort((a, b) =>
      sortFnByAuditPointNo(
        { auditPointNo: a.auditPointNumber },
        { auditPointNo: b.auditPointNumber }
      )
    )

    const usedAuditPoints = deviations.filter(deviation => {
      return links.some(link =>
        link.actionPlanItems.some(
          item => item.actionPlanItemId === deviation?.id
        )
      )
    })

    type GroupedDeviations = {
      [key: string]: typeof deviations
    }

    const unusedAuditPoints = deviations.filter(deviation => {
      return !links.some(link =>
        link.actionPlanItems.some(
          item => item.actionPlanItemId === deviation?.id
        )
      )
    })

    const groupedDeviationsUsedAuditPoints: GroupedDeviations =
      usedAuditPoints.reduce((acc, deviation) => {
        const auditPointId = deviation.auditPointId ?? 'Unknown Audit Point'
        if (!acc[auditPointId]) {
          acc[auditPointId] = []
        }
        acc[auditPointId].push(deviation)
        return acc
      }, {} as GroupedDeviations)

    const groupedDeviationsUnUsedAuditPoints: GroupedDeviations =
      unusedAuditPoints.reduce((acc, deviation) => {
        const auditPointId = deviation.auditPointId ?? 'Unknown Audit Point'
        if (!acc[auditPointId]) {
          acc[auditPointId] = []
        }
        acc[auditPointId].push(deviation)
        return acc
      }, {} as GroupedDeviations)

    const onDeviationFilterChange = (
      event: CustomEvent<{ chipId: string; value: string }>
    ) => {
      const detail = event.detail

      if (event.detail.value === 'all') {
        setData({
          ...data,
          actionPlanItems: deviations.map(deviation => ({
            auditId: audit.id,
            actionPlanItemId: deviation.id ?? 0,
            actionPlanId: deviation.actionPlanId
          }))
        })
      } else if (event.detail.value === 'open') {
        const openActionPlans = deviations.filter(
          deviation => deviation.approvalDate === null
        )

        if (data.actionPlanItems?.length === openActionPlans.length) {
          setData({
            ...data,
            actionPlanItems: []
          })
        } else {
          setData({
            ...data,
            actionPlanItems: openActionPlans.map(deviation => ({
              auditId: audit.id,
              actionPlanItemId: deviation.id ?? 0,
              actionPlanId: deviation.actionPlanId
            }))
          })
        }
      } else if (event.detail.value === 'none') {
        setData({
          ...data,
          actionPlanItems: []
        })
      }
    }

    return (
      <TdsModal
        header={t('create-link', { ns: 'common' })}
        selector={selector}
        size='lg'
        actions-position='static'
      >
        <>
          <span slot='body'>
            <TdsTextField
              labelPosition='outside'
              label={t('name', { ns: 'common' })}
              placeholder={t('link-name', { ns: 'common' })}
              onTdsChange={e => {
                setData({ ...data, name: e.target.value })
              }}
              onBlur={e => setStateName(checkStringLength(e.target.value, 3))}
              state={!stateName ? 'error' : undefined}
              helper={
                !stateName
                  ? t('validation-error', { ns: 'errors', chars: '3' })
                  : undefined
              }
              value={data.name}
              tdsAriaLabel='Name'
            />
            <StyledModalContent>
              <p className='tds-detail-04'>{t('select-deviations-for-link')}</p>
              <StyledFieldset>
                <TdsChip
                  onTdsChange={onDeviationFilterChange}
                  name='deviations-filter'
                  type='radio'
                  value='all'
                  chipId='all'
                  tdsAriaLabel={t('all', { ns: 'common' })}
                >
                  <TdsIcon slot='prefix' name='burger' size='16' />
                  <span slot='label'>{t('all', { ns: 'common' })}</span>
                </TdsChip>
                <TdsChip
                  chipId='open'
                  onTdsChange={onDeviationFilterChange}
                  name='deviations-filter'
                  type='radio'
                  value='open'
                  tdsAriaLabel={t('open', { ns: 'common' })}
                >
                  <TdsIcon slot='prefix' name='document_check' size='16' />
                  <span slot='label'>{t('open', { ns: 'common' })}</span>
                </TdsChip>
                <TdsChip
                  chipId='none'
                  onTdsChange={onDeviationFilterChange}
                  name='deviations-filter'
                  type='radio'
                  value='none'
                  tdsAriaLabel={t('none', { ns: 'common' })}
                >
                  <TdsIcon slot='prefix' name='document' size='16' />
                  <span slot='label'>{t('none', { ns: 'common' })}</span>
                </TdsChip>
              </StyledFieldset>
              <StyledDeviationsList>
                {Object.keys(groupedDeviationsUnUsedAuditPoints).map(
                  auditPointId => (
                    <Fragment key={auditPointId}>
                      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                      <li>
                        <FlexContainer>
                          <b>
                            {
                              audit.auditPoints.find(
                                ap => ap.id === parseInt(auditPointId, 10)
                              )?.auditPointNo
                            }
                          </b>
                          <span>
                            {removeHtmlTagsFromString(
                              t(
                                audit.auditPoints.find(
                                  ap => ap.id === parseInt(auditPointId, 10)
                                )?.translationGuid ?? ''
                              )
                            )}
                          </span>
                        </FlexContainer>
                      </li>
                      {groupedDeviationsUnUsedAuditPoints[auditPointId].map(
                        deviation => {
                          return (
                            // eslint-disable-next-line jsx-a11y/no-redundant-roles
                            <li key={deviation.id}>
                              <StyledDiv>
                                <TdsCheckbox
                                  tdsAriaLabel={`Deviation ${deviation.id}`}
                                  id={`deviation-${deviation.id}`}
                                  checked={
                                    !!data.actionPlanItems?.find(
                                      api =>
                                        api.actionPlanItemId === deviation.id
                                    )
                                  }
                                  onTdsChange={() => {
                                    const api = data.actionPlanItems?.find(
                                      api =>
                                        api.actionPlanItemId === deviation.id
                                    )

                                    if (api) {
                                      setData({
                                        ...data,
                                        actionPlanItems:
                                          data.actionPlanItems?.filter(
                                            api =>
                                              api.actionPlanItemId !==
                                              deviation.id
                                          )
                                      })
                                    } else {
                                      const newActionPlans = [
                                        ...(data.actionPlanItems || [])
                                      ]
                                      newActionPlans.push({
                                        auditId: audit.id,
                                        actionPlanItemId: deviation.id ?? 0,
                                        actionPlanId: deviation.actionPlanId
                                      })

                                      setData({
                                        ...data,
                                        actionPlanItems: newActionPlans
                                      })
                                    }
                                  }}
                                >
                                  <div slot='label'>
                                    <StyledParagraph>
                                      {deviation.deviation}
                                    </StyledParagraph>

                                    <StyledParagraph>
                                      {deviation.proposedActions ?? ''}
                                    </StyledParagraph>
                                    <StyledParagraph>
                                      {` ${t('responsible')}: ${
                                        deviation.responsible ?? ''
                                      }`}
                                    </StyledParagraph>
                                    {deviation.expirationDate && (
                                      <StyledParagraph>
                                        {` ${t('expiry-date')}: ${formatISODate(
                                          new Date(
                                            deviation.expirationDate ?? ''
                                          )
                                        )}`}
                                      </StyledParagraph>
                                    )}
                                  </div>
                                </TdsCheckbox>
                              </StyledDiv>
                            </li>
                          )
                        }
                      )}
                    </Fragment>
                  )
                )}
                {Object.keys(groupedDeviationsUsedAuditPoints).map(
                  auditPointId => (
                    <Fragment key={auditPointId}>
                      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                      <li>
                        <FlexContainer>
                          <b>
                            {
                              audit.auditPoints.find(
                                ap => ap.id === parseInt(auditPointId, 10)
                              )?.auditPointNo
                            }
                          </b>
                          <span>
                            {removeHtmlTagsFromString(
                              t(
                                audit.auditPoints.find(
                                  ap => ap.id === parseInt(auditPointId, 10)
                                )?.translationGuid ?? ''
                              )
                            )}
                          </span>
                        </FlexContainer>
                      </li>
                      {groupedDeviationsUsedAuditPoints[auditPointId].map(
                        deviation => {
                          return (
                            // eslint-disable-next-line jsx-a11y/no-redundant-roles
                            <li key={deviation.id}>
                              <StyledDiv>
                                <TdsCheckbox
                                  id={`deviation-${deviation.id}`}
                                  checked={
                                    !!data.actionPlanItems?.find(
                                      api =>
                                        api.actionPlanItemId === deviation.id
                                    )
                                  }
                                  onTdsChange={() => {
                                    const api = data.actionPlanItems?.find(
                                      api =>
                                        api.actionPlanItemId === deviation.id
                                    )

                                    if (api) {
                                      setData({
                                        ...data,
                                        actionPlanItems:
                                          data.actionPlanItems?.filter(
                                            api =>
                                              api.actionPlanItemId !==
                                              deviation.id
                                          )
                                      })
                                    } else {
                                      const newActionPlans = [
                                        ...(data.actionPlanItems || [])
                                      ]
                                      newActionPlans.push({
                                        auditId: audit.id,
                                        actionPlanItemId: deviation.id ?? 0,
                                        actionPlanId: deviation.actionPlanId
                                      })

                                      setData({
                                        ...data,
                                        actionPlanItems: newActionPlans
                                      })
                                    }
                                  }}
                                >
                                  <div slot='label'>
                                    <StyledParagraph>
                                      {deviation.deviation}
                                    </StyledParagraph>
                                    <StyledParagraph>
                                      {deviation.proposedActions ?? ''}
                                    </StyledParagraph>
                                    <StyledParagraph>
                                      {` ${t('responsible')}: ${
                                        deviation.responsible ?? ''
                                      }`}
                                    </StyledParagraph>
                                    {deviation.expirationDate && (
                                      <StyledParagraph>
                                        {` ${t('expiry-date')}: ${formatISODate(
                                          new Date(
                                            deviation.expirationDate ?? ''
                                          )
                                        )}`}
                                      </StyledParagraph>
                                    )}
                                  </div>
                                </TdsCheckbox>
                              </StyledDiv>
                            </li>
                          )
                        }
                      )}
                    </Fragment>
                  )
                )}
              </StyledDeviationsList>
              {!stateActionPlans && (
                <StyledMessageWrapper>
                  <TdsMessage
                    className='tds-u-mt1'
                    variant='error'
                    modeVariant={'secondary'}
                  >
                    {t('select-audit-point')}
                  </TdsMessage>
                </StyledMessageWrapper>
              )}
            </StyledModalContent>
          </span>
          <span slot='actions' className='tds-u-flex tds-u-gap2'>
            <TdsButton size='sm' text='Create' onClick={() => handleSubmit()} />
            <TdsButton
              size='sm'
              text='Cancel'
              variant='secondary'
              onClick={() => closeModal()}
            />
          </span>
        </>
      </TdsModal>
    )
  }
)
